import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getBatchList,
  getBatchDetail,
  approveBatch,
  rejectBatch,
  withdrawBatch,
} from "../../../services/panel/panelServices";
import { useAuth } from "../../../context/AuthContext";
import toast from "../../../utils/toast";

const formatAmount = (amt) => `₹${Number(amt || 0).toLocaleString("en-IN")}`;

// "2026-10-30T18:20:55.083" → "30 Oct, 6:20 pm"
const formatDateTime = (value) => {
  const d = new Date(value);
  return isNaN(d)
    ? value || ""
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      });
};

// Batch status: 1 Pending, 2 Approved, 3 Processing, 4 Processed, 5 Rejected, 6 Withdrawn
const BATCH_PILL = {
  1: "pill-pending",
  2: "pill-info",
  3: "pill-info",
  4: "pill-success",
  5: "pill-failed",
  6: "pill-neutral",
};

// Item status: 0 Queued, 1 Pending at biller, 2 Success, 3 Failed
const ITEM_PILL = {
  0: "pill-neutral",
  1: "pill-pending",
  2: "pill-success",
  3: "pill-failed",
};

// Rejected chip covers Rejected (5) and Withdrawn (6) — fetched as status 0 and filtered
const FILTERS = [
  { key: "pending", label: "Pending", status: 1 },
  { key: "all", label: "All", status: 0 },
  { key: "rejected", label: "Rejected", status: 0, only: [5, 6] },
  { key: "paid", label: "Paid", status: 4 },
];

const MIN_REASON = 5;

const batchPillText = (b) =>
  b.status === 1
    ? b.approvalCount > 0
      ? `Approval ${b.approvalCount + 1} of ${b.requiredApprovals}`
      : "Awaiting approval"
    : b.statusText;

// Maker → approver step(s) → BBPS, built from batch + audit rows
const buildChain = (batch, audit) => {
  const approvals = audit.filter((a) => a.action === "APPROVE");
  const steps = [
    {
      state: "ok",
      dot: "✓",
      role: "Maker",
      who: batch.makerName,
      when: formatDateTime(batch.entryDate),
    },
  ];
  let waitingShown = false;
  for (let i = 0; i < batch.requiredApprovals; i++) {
    const role =
      batch.requiredApprovals === 1
        ? "Approver · Checker or Admin"
        : i === 0
          ? "Approver 1 · Checker"
          : "Approver 2 · Fleet Owner";
    const done = approvals[i];
    if (done) {
      steps.push({
        state: "ok",
        dot: "✓",
        role,
        who: done.actorName,
        when: formatDateTime(done.entryDate),
      });
    } else if (!waitingShown) {
      waitingShown = true;
      steps.push(
        batch.status === 5
          ? { state: "bad", dot: "✕", role, who: "Rejected", when: "" }
          : batch.status === 6
            ? { state: "", dot: i + 1, role, who: "Withdrawn", when: "" }
            : { state: "wait", dot: i + 1, role, who: "Waiting", when: "" },
      );
    } else {
      steps.push({ state: "", dot: i + 1, role, who: "—", when: "" });
    }
  }
  steps.push({
    state:
      batch.status === 4 ? "ok" : [2, 3].includes(batch.status) ? "wait" : "",
    dot: "₹",
    role: "BBPS payment",
    who:
      batch.status === 4
        ? "Paid"
        : [2, 3].includes(batch.status)
          ? "Sending"
          : "Not sent",
    when: "",
  });
  return steps;
};

export default function Approval() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [filterKey, setFilterKey] = useState("pending");
  const [isBatchData, setBatchData] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [listLoading, setListLoading] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [isDetailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [remark, setRemark] = useState("");
  const [remarkError, setRemarkError] = useState("");
  const [acting, setActing] = useState("");
  const [insufficientFund, setInsufficientFund] = useState(false);
  const remarkRef = useRef(null);
  // Ignore an older batch's response if the user clicked another one meanwhile
  const latestIdRef = useRef(null);

  useEffect(() => {
    // Coming from Bill review: open the batch that was just created
    fetchBatchList(location.state?.batchId);
  }, [filterKey]);

  const fetchBatchList = async (keepId) => {
    const filter = FILTERS.find((f) => f.key === filterKey);
    setListLoading(true);
    try {
      const res = await getBatchList({ status: filter.status });

      if (res?.statuscode === 1) {
        const records = (res?.records || []).filter(
          (b) => !filter.only || filter.only.includes(b.status),
        );
        setBatchData(records);
        // pendingCount is the same whatever the filter
        setPendingCount(res?.pendingCount || 0);
        const nextId = records.some((b) => b.id === keepId)
          ? keepId
          : records[0]?.id;
        if (nextId) handleSelectBatch(nextId);
        else {
          setSelectedId(null);
          setDetailData(null);
        }
      } else {
        setBatchData([]);
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch batches",
        "error",
      );
    } finally {
      setListLoading(false);
    }
  };

  const fetchBatchDetail = async (id) => {
    latestIdRef.current = id;
    setDetailLoading(true);
    try {
      const res = await getBatchDetail(id);

      if (latestIdRef.current !== id) return;

      if (res?.statuscode === 1) {
        setDetailData(res);
      } else {
        setDetailData(null);
        toast.error(res?.msg || "Batch not found.");
      }
    } catch (err) {
      console.log(err.response?.data?.msg || "Failed to fetch batch", "error");
    } finally {
      if (latestIdRef.current === id) setDetailLoading(false);
    }
  };

  const handleSelectBatch = (id) => {
    setSelectedId(id);
    setRemark("");
    setRemarkError("");
    setInsufficientFund(false);
    fetchBatchDetail(id);
  };

  // Refetch list + detail after every action instead of patching by hand
  const refreshAfterAction = () => fetchBatchList(selectedId);

  const handleApprove = async () => {
    setActing("approve");
    try {
      const payload = remark.trim() ? { Remark: remark.trim() } : {};
      const res = await approveBatch(selectedId, payload);
      if (res?.statuscode === 1) {
        toast.success(res?.msg || "Batch approved.");
        setRemark("");
        refreshAfterAction();
      } else {
        toast.error(res?.msg || "Failed to approve batch.");
        setInsufficientFund(!!res?.insufficientFund);
        fetchBatchDetail(selectedId);
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to approve batch.");
    } finally {
      setActing("");
    }
  };

  const handleReject = async () => {
    if (remark.trim().length < MIN_REASON) {
      setRemarkError(
        "Write a reason (at least 5 characters) so the Maker knows what to fix.",
      );
      remarkRef.current?.focus();
      return;
    }
    setActing("reject");
    try {
      const res = await rejectBatch(selectedId, { Remark: remark.trim() });
      if (res?.statuscode === 1) {
        toast.success(res?.msg || "Batch rejected.");
        setRemark("");
        refreshAfterAction();
      } else {
        toast.error(res?.msg || "Failed to reject batch.");
        fetchBatchDetail(selectedId);
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to reject batch.");
    } finally {
      setActing("");
    }
  };

  const handleWithdraw = async () => {
    setActing("withdraw");
    try {
      const res = await withdrawBatch(selectedId);
      if (res?.statuscode === 1) {
        toast.success(res?.msg || "Batch withdrawn.");
        refreshAfterAction();
      } else {
        toast.error(res?.msg || "Failed to withdraw batch.");
        fetchBatchDetail(selectedId);
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to withdraw batch.");
    } finally {
      setActing("");
    }
  };

  const batch = isDetailData?.batch;
  const items = isDetailData?.items || [];
  const audit = isDetailData?.audit || [];
  const walletAfter =
    Number(isDetailData?.walletBalance || 0) - Number(batch?.totalAmount || 0);
  const isFinalApproval =
    batch && batch.approvalCount + 1 >= batch.requiredApprovals;
  const busy = !!acting || detailLoading;

  return (
    <div className="screen active" id="screen-approval">
      <div className="page-head">
        <div>
          <h1>Approval</h1>
          <div className="ph-sub">
            Maker-checker: no one can approve a batch they created
          </div>
        </div>
      </div>

      <div className="role-banner info">
        <div className="avatar-sm">
          {(user?.name || "?")
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((w) => w[0].toUpperCase())
            .join("")}
        </div>
        <div style={{ flex: 1 }}>
          <strong>
            {user?.name} ({user?.roleName})
          </strong>{" "}
          · <strong>{pendingCount}</strong> batch(es) waiting for approval.
          Batches you created or already approved are locked.
        </div>
      </div>

      <div className="appr-layout">
        <div className="card appr-queue-card">
          <div className="card-title">Payment batches</div>
          <div className="card-sub">
            Select a batch to see its approval trail
          </div>
          <div className="chip-row" style={{ marginBottom: 12 }}>
            {FILTERS.map((f) => (
              <div
                key={f.key}
                className={`fchip ${filterKey === f.key ? "active" : ""}`}
                onClick={() => !listLoading && setFilterKey(f.key)}
              >
                {f.key === "pending" ? `${f.label} (${pendingCount})` : f.label}
              </div>
            ))}
          </div>
          <div style={{ opacity: listLoading ? 0.5 : 1 }}>
            {listLoading && !isBatchData.length ? (
              <div className="empty-note">Loading batches...</div>
            ) : isBatchData.length === 0 ? (
              <div className="empty-note">
                Nothing here. New batches appear when a maker submits from Bill
                review.
              </div>
            ) : (
              isBatchData.map((b) => (
                <button
                  key={b.id}
                  className={`q-item ${selectedId === b.id ? "active" : ""}`}
                  onClick={() => handleSelectBatch(b.id)}
                >
                  <div className="q-top">
                    <span className="q-id">{b.batchNo}</span>
                    <span
                      className={`pill ${BATCH_PILL[b.status] || "pill-neutral"}`}
                    >
                      {batchPillText(b)}
                    </span>
                  </div>
                  <div className="q-meta">
                    {b.billCount} bills · by {b.makerName} ·{" "}
                    {formatDateTime(b.entryDate)}
                  </div>
                  <div className="q-amt">{formatAmount(b.totalAmount)}</div>
                </button>
              ))
            )}
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          <div className="card" style={{ opacity: detailLoading ? 0.6 : 1 }}>
            {!batch ? (
              <div className="empty-note">
                {detailLoading ? "Loading batch..." : "Select a batch"}
              </div>
            ) : (
              <>
                <div className="card-head-row">
                  <div>
                    <div className="card-title">
                      Batch <span className="mono">{batch.batchNo}</span>
                    </div>
                    <div className="card-sub">
                      Created by {batch.makerName} (Maker) ·{" "}
                      {formatDateTime(batch.entryDate)}
                    </div>
                  </div>
                  <span
                    className={`pill ${BATCH_PILL[batch.status] || "pill-neutral"}`}
                  >
                    {batchPillText(batch)}
                  </span>
                </div>

                <div className="chain">
                  {buildChain(batch, audit).map((s, i) => (
                    <div key={i} className={`chain-step ${s.state}`}>
                      <div className="chain-dot">{s.dot}</div>
                      <div className="chain-role">{s.role}</div>
                      <div className="chain-who">{s.who}</div>
                      <div className="chain-when">{s.when}</div>
                    </div>
                  ))}
                </div>

                <div className="table-wrap">
                  <table className="data-table">
                    <tbody>
                      <tr>
                        <th>Biller</th>
                        <th>Account</th>
                        <th>Status</th>
                        <th className="tright">Amount</th>
                      </tr>
                      {items.map((it) => (
                        <tr key={it.id}>
                          <td>
                            {it.billerName}
                            {it.nickname && (
                              <div
                                style={{
                                  fontSize: "10.5px",
                                  color: "var(--text-faint)",
                                }}
                              >
                                {it.nickname}
                              </div>
                            )}
                          </td>
                          <td className="mono">{it.accountNumber}</td>
                          <td>
                            <span
                              className={`pill ${ITEM_PILL[it.status] || "pill-neutral"}`}
                            >
                              {it.statusText}
                            </span>
                          </td>
                          <td className="tright mono">
                            {formatAmount(it.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="appr-sum">
                  <div>
                    <span>Batch total</span>
                    <b>{formatAmount(batch.totalAmount)}</b>
                  </div>
                  <div>
                    <span>Approvals</span>
                    <b>
                      {batch.approvalCount} / {batch.requiredApprovals}
                    </b>
                  </div>
                  <div>
                    <span>Wallet after payment</span>
                    <b
                      style={{
                        color: walletAfter < 0 ? "var(--red)" : "inherit",
                      }}
                    >
                      {walletAfter < 0 ? "−" : ""}
                      {formatAmount(Math.abs(walletAfter))}
                    </b>
                  </div>
                </div>

                {batch.status === 5 && (
                  <div className="appr-block bad">
                    Rejected by {batch.rejectedByName || "approver"}:{" "}
                    {batch.rejectReason}
                  </div>
                )}
                {batch.status === 6 && (
                  <div className="appr-block">
                    Withdrawn by {batch.makerName}. Its bills are free to add to
                    a new batch.
                  </div>
                )}
                {isDetailData?.actionNote && (
                  <div className="appr-block">{isDetailData.actionNote}</div>
                )}
                {(insufficientFund ||
                  (walletAfter < 0 && isDetailData?.canApprove)) && (
                  <div
                    className="appr-block warn"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <span>Wallet balance is lower than the batch total.</span>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => navigate("/company-add-fund")}
                    >
                      Add fund
                    </button>
                  </div>
                )}

                {(isDetailData?.canApprove || isDetailData?.canReject) && (
                  <div className="field">
                    <label>
                      Remark (optional to approve, required to reject)
                    </label>
                    <textarea
                      ref={remarkRef}
                      rows={2}
                      value={remark}
                      onChange={(e) => {
                        setRemark(e.target.value);
                        setRemarkError("");
                      }}
                      placeholder="e.g. Head office bill amount doesn't match the meter reading"
                      style={
                        remarkError ? { borderColor: "#dc2626" } : undefined
                      }
                    />
                    {remarkError && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {remarkError}
                      </span>
                    )}
                  </div>
                )}

                <div className="appr-actions">
                  {isDetailData?.canApprove && (
                    <button
                      className="btn btn-primary"
                      onClick={handleApprove}
                      disabled={busy}
                    >
                      {acting === "approve"
                        ? "Approving..."
                        : isFinalApproval
                          ? "Approve & pay"
                          : `Approve (${batch.approvalCount + 1} of ${batch.requiredApprovals})`}
                    </button>
                  )}
                  {isDetailData?.canReject && (
                    <button
                      className="btn btn-danger"
                      onClick={handleReject}
                      disabled={busy}
                    >
                      {acting === "reject" ? "Rejecting..." : "Reject"}
                    </button>
                  )}
                  {isDetailData?.canWithdraw && (
                    <button
                      className="btn btn-ghost"
                      onClick={handleWithdraw}
                      disabled={busy}
                    >
                      {acting === "withdraw" ? "Withdrawing..." : "Withdraw"}
                    </button>
                  )}
                  {isDetailData?.canProcess && (
                    <button
                      className="btn btn-primary"
                      onClick={() =>
                        navigate("/processing", {
                          state: { batchId: batch.id },
                        })
                      }
                      disabled={busy}
                    >
                      Send to BBPS
                    </button>
                  )}
                </div>

                <div className="audit">
                  <div
                    className="card-title"
                    style={{ fontSize: 13, marginBottom: 6 }}
                  >
                    Audit trail
                  </div>
                  {audit.map((a) => (
                    <div key={a.id} className="audit-row">
                      <time>{formatDateTime(a.entryDate)}</time>
                      <div>
                        <strong>{a.actorName}</strong> · {a.action}
                        {a.remark && ` · ${a.remark}`}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="card" style={{ marginTop: 18 }}>
            <div className="card-title">Approval rules</div>
            <div className="card-sub">Applied to every batch</div>
            <div className="rules">
              {[
                "A Maker creates batches. Only a Checker or the Fleet Owner can approve them.",
                "Nobody can approve a batch they created.",
                // Limit is fixed at ₹50,000 on the server for now
                `Batches above ${formatAmount(50000)} need two approvals: a Checker first, then the Fleet Owner.`,
                "A bill can sit in only one open batch at a time, so it can't be paid twice.",
                "Final approval is blocked if the wallet balance is lower than the batch total.",
                "Rejecting needs a written reason. Every action is stored in the audit trail.",
              ].map((r) => (
                <div key={r} className="rule">
                  <i>✓</i>
                  <div>{r}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
